import * as XLSX from 'xlsx';
import { parseCountryList, parseInitiatives } from './parsers.js';
import { parseResultCenterCodes, CENTER_DIRECTORY } from './partnershipParsers.js';

// ── LLM Column Manifest ─────────────────────────────────────────────
// Single source of truth for which result fields can be sent to the LLM.
// `locked` fields are always included; `default` fields are on by default.
export const LLM_COLUMN_GROUPS = [
  { group: 'Always included', columns: [
    { key: 'id', label: 'Result ID', locked: true, default: true },
  ]},
  { group: 'Content', columns: [
    { key: 'title', label: 'Title', locked: false, default: true },
    { key: 'description', label: 'Description', locked: false, default: true },
  ]},
  { group: 'Classification', columns: [
    { key: 'initiative', label: 'Submitting Program', locked: false, default: false },
    { key: 'type', label: 'Type', locked: false, default: false },
    { key: 'level', label: 'Level', locked: false, default: false },
    { key: 'status', label: 'Status', locked: false, default: false },
    { key: 'kpType', label: 'KP Type', locked: false, default: false },
    { key: 'fundingType', label: 'Funding Type', locked: false, default: false },
    { key: 'isKRS', label: 'Is KRS', locked: false, default: false },
  ]},
  { group: 'Impact Tags', columns: [
    { key: 'genderLevel', label: 'Gender Tag', locked: false, default: false },
    { key: 'climateLevel', label: 'Climate Tag', locked: false, default: false },
    { key: 'nutritionLevel', label: 'Nutrition Tag', locked: false, default: false },
    { key: 'biodiversityLevel', label: 'Biodiversity Tag', locked: false, default: false },
    { key: 'povertyLevel', label: 'Poverty Tag', locked: false, default: false },
  ]},
  { group: 'Geography', columns: [
    { key: 'countries', label: 'Countries', locked: false, default: false },
    { key: 'regions', label: 'Regions', locked: false, default: false },
  ]},
  { group: 'Organization', columns: [
    { key: 'leadCenter', label: 'Lead Center', locked: false, default: false },
    { key: 'contributingCenters', label: 'Contributing Centers', locked: false, default: false },
    { key: 'partners', label: 'Partners', locked: false, default: false },
    { key: 'partnersKP', label: 'Partners (KP)', locked: false, default: false },
  ]},
  { group: 'Theory of Change', columns: [
    { key: 'tocMapping', label: 'ToC Mapping', locked: false, default: false },
    { key: 'tocMappingType', label: 'ToC Mapping Type', locked: false, default: false },
    { key: 'indicatorUsed', label: 'Indicator Used', locked: false, default: false },
    { key: 'actualAchieved', label: 'Actual Achieved', locked: false, default: false },
  ]},
  { group: 'Innovation', columns: [
    { key: 'innovationUseLevel', label: 'Use Level', locked: false, default: false },
    { key: 'readinessLevel', label: 'Readiness Level', locked: false, default: false },
  ]},
  { group: 'Funding', columns: [
    { key: 'nonPooledProjects', label: 'Non-pooled Projects', locked: false, default: false },
    { key: 'w3ProjectTitle', label: 'W3 Project Title', locked: false, default: false },
  ]},
];

// Derive default column set from the manifest
export const DEFAULT_LLM_COLUMNS = new Set(
  LLM_COLUMN_GROUPS.flatMap(g => g.columns).filter(c => c.default).map(c => c.key)
);

// All locked column keys (always included regardless of user selection)
const LOCKED_COLUMNS = new Set(
  LLM_COLUMN_GROUPS.flatMap(g => g.columns).filter(c => c.locked).map(c => c.key)
);

// Extract the first URL from an evidence string.
// Evidence strings look like: "• link: https://...; gender related? yes; ..."
// or sometimes just: "https://..."
function extractEvidenceUrl(evidenceStr) {
  if (!evidenceStr) return null;
  const linkMatch = evidenceStr.match(/link:\s*(https?:\/\/[^;\s]+)/);
  if (linkMatch) return linkMatch[1];
  const urlMatch = evidenceStr.match(/(https?:\/\/[^;\s]+)/);
  return urlMatch ? urlMatch[1] : null;
}

// Extract text content for LLM processing — send all filtered results with selected columns.
// Returns { items, urlMap } where urlMap maps result codes to their first evidence URL.
export function extractTextForLLM(results, enabledColumns = DEFAULT_LLM_COLUMNS) {
  const items = [];
  const urlMap = {};

  // Build the active column set: locked columns are always present
  const active = new Set([...LOCKED_COLUMNS, ...enabledColumns]);

  for (const r of results) {
    const id = String(r.resultCode);
    const item = { id };

    if (active.has('title')) item.title = r.title;
    if (active.has('description')) item.description = r.description;
    if (active.has('initiative')) item.initiative = r.initiative;
    if (active.has('type')) item.type = r.type;
    if (active.has('level')) item.level = r.level;
    if (active.has('status')) item.status = r.status;
    if (active.has('kpType')) item.kpType = r.kpType;
    if (active.has('fundingType')) item.fundingType = r.fundingType;
    if (active.has('isKRS')) item.isKRS = r.isKRS;
    if (active.has('genderLevel')) item.genderLevel = r.genderLevel;
    if (active.has('climateLevel')) item.climateLevel = r.climateLevel;
    if (active.has('nutritionLevel')) item.nutritionLevel = r.nutritionLevel;
    if (active.has('biodiversityLevel')) item.biodiversityLevel = r.biodiversityLevel;
    if (active.has('povertyLevel')) item.povertyLevel = r.povertyLevel;
    if (active.has('countries')) item.countries = r.countries;
    if (active.has('regions')) item.regions = r.regions;
    if (active.has('leadCenter')) item.leadCenter = r.leadCenter;
    if (active.has('contributingCenters')) item.contributingCenters = r.contributingCenters;
    if (active.has('partners')) item.partners = r.partners;
    if (active.has('partnersKP')) item.partnersKP = r.partnersKP;
    if (active.has('tocMapping')) item.tocMapping = r.tocMapping;
    if (active.has('tocMappingType')) item.tocMappingType = r.tocMappingType;
    if (active.has('indicatorUsed')) item.indicatorUsed = r.indicatorUsed;
    if (active.has('actualAchieved')) item.actualAchieved = r.actualAchieved;
    if (active.has('innovationUseLevel')) item.innovationUseLevel = r.innovationUseLevel;
    if (active.has('readinessLevel')) item.readinessLevel = r.readinessLevel;
    if (active.has('nonPooledProjects')) item.nonPooledProjects = r.nonPooledProjects;
    if (active.has('w3ProjectTitle')) item.w3ProjectTitle = r.w3ProjectTitle;

    items.push(item);
    if (r.pdfLink) urlMap[id] = r.pdfLink;
  }

  return { items, urlMap };
}

// Get unique values for filter dropdowns
export function getUniqueValues(results) {
  const programs = new Set();
  const years = new Set();
  const levels = new Set();
  const types = new Set();
  const countries = new Set();
  const regions = new Set();
  const statuses = new Set();
  const reportingPhases = new Set();
  const leadCenters = new Set();
  const centerInvolvement = new Set();

  results.forEach(result => {
    // Pre-compute canonical center codes for filtering
    if (!result.centerCodes) {
      result.centerCodes = parseResultCenterCodes(result);
    }
    result.centerCodes.forEach(code => centerInvolvement.add(code));
    // Programs — use initiative (primary program code)
    if (result.initiative) programs.add(result.initiative);

    // Also include contributing initiatives
    if (result.contributingInitiatives) {
      result.contributingInitiatives.forEach(init => {
        if (init) programs.add(init);
      });
    }

    // Years
    if (result.year) years.add(result.year);

    // Levels
    if (result.level) levels.add(result.level);

    // Types
    if (result.type) types.add(result.type);

    // Countries — use pre-parsed array if available
    const countryArr = result.countriesParsed || (result.countries ? parseCountryList(result.countries) : []);
    countryArr.forEach(country => countries.add(country));

    // Regions — use pre-parsed array if available
    const regionArr = result.regionsParsed || (result.regions ? parseCountryList(result.regions) : []);
    regionArr.forEach(region => regions.add(region));

    // Status
    if (result.status) statuses.add(result.status);

    // Reporting Phase (V22)
    if (result.reportingPhase) reportingPhases.add(result.reportingPhase);

    // Lead Center
    if (result.leadCenter) leadCenters.add(result.leadCenter);
  });

  return {
    programs: [...programs].sort(),
    years: [...years].sort((a, b) => b - a),
    levels: [...levels].sort(),
    types: [...types].sort(),
    countries: [...countries].sort(),
    regions: [...regions].sort(),
    statuses: [...statuses].sort(),
    reportingPhases: [...reportingPhases].sort(),
    leadCenters: [...leadCenters].sort(),
    centerInvolvement: [...centerInvolvement].sort().map(code => ({
      value: code,
      label: CENTER_DIRECTORY.get(code) ? `${code} - ${CENTER_DIRECTORY.get(code)}` : code,
    })),
  };
}

export function mapExportRows(data) {
  return data.map(r => ({
    'Result Code': r.resultCode,
    'ID': r.id,
    'Year': r.year,
    'Status': r.status,
    'Funding Type': r.fundingType,
    'Initiative': r.initiative,
    'Primary Submitter': r.primarySubmitter,
    'Type': r.type,
    'Level': r.level,
    'Title': r.title,
    'Description': r.description,
    'KP Type': r.kpType || '',
    'Is KRS': r.isKRS || '',
    'Countries': r.countries,
    'Regions': r.regions,
    'Gender Tag Level': r.genderLevel,
    'Climate Tag Level': r.climateLevel,
    'Nutrition Tag Level': r.nutritionLevel,
    'Biodiversity Tag Level': r.biodiversityLevel,
    'Poverty Tag Level': r.povertyLevel,
    'Lead Center': r.leadCenter,
    'Contributing Centers': r.contributingCenters,
    'Contributing Initiatives': Array.isArray(r.contributingInitiatives) ? r.contributingInitiatives.join('; ') : (r.contributingInitiatives || ''),
    'Partners': r.partners,
    'Partners KP': r.partnersKP,
    'ToC Mapping': r.tocMapping,
    'ToC Alignment': r.tocMappingType || '',
    'Indicator Used': r.indicatorUsed || '',
    'Actual Achieved': r.actualAchieved || '',
    'Innovation Use Level': r.innovationUseLevel || '',
    'Readiness Level': r.readinessLevel || '',
    'Linked Results': r.linkedResults || '',
    'W3 Project Title': r.w3ProjectTitle,
    'W3 Project Info': r.w3ProjectInfo,
    'Reporting Phase': r.reportingPhase || '',
    'Evidence 1': r.evidence?.[0] || '',
    'Evidence 2': r.evidence?.[1] || '',
    'Evidence 3': r.evidence?.[2] || '',
    'Evidence 4': r.evidence?.[3] || '',
    'Evidence 5': r.evidence?.[4] || '',
    'Evidence 6': r.evidence?.[5] || '',
  }));
}

/**
 * Map result objects to a focused set of metadata columns for chart-level exports.
 * Lighter than mapExportRows (13 columns vs 35) — covers the fields users need
 * when drilling into chart data: result code, title, description, typology, geo focus, partnerships.
 */
export function mapChartExportRows(data) {
  return data.map(r => ({
    'Result Code': r.resultCode,
    'Title': r.title,
    'Description': r.description,
    'Type': r.type,
    'Level': r.level,
    'Status': r.status,
    'Funding Type': r.fundingType,
    'Initiative': r.initiative,
    'Countries': r.countries,
    'Regions': r.regions,
    'Lead Center': r.leadCenter,
    'Contributing Centers': r.contributingCenters,
    'Partners': r.partners,
  }));
}

/**
 * Export a data subset to XLSX and trigger download.
 * @param {Array} data - Array of result objects to export
 * @param {string} filename - Name for the downloaded file
 * @param {string} [subsetLabel] - Label describing what this subset represents
 */
export async function exportDataSubset(data, filename, subsetLabel) {
  if (!data || data.length === 0) return;

  const XLSX_LIB = await import('xlsx');
  const exportRows = mapExportRows(data);

  const ws = XLSX_LIB.utils.json_to_sheet(exportRows);
  const wb = XLSX_LIB.utils.book_new();
  const sheetName = (subsetLabel || 'Data').slice(0, 31);
  XLSX_LIB.utils.book_append_sheet(wb, ws, sheetName);
  XLSX_LIB.writeFile(wb, `${filename}.xlsx`);
}

/**
 * Export a data subset to CSV and trigger download.
 * @param {Array} data - Array of result objects to export
 * @param {string} filename - Name for the downloaded file
 */
export function exportDataSubsetCSV(data, filename) {
  if (!data || data.length === 0) return;

  const exportRows = mapExportRows(data);
  const headers = Object.keys(exportRows[0]);

  const escapeCsvField = (val) => {
    const str = val == null ? '' : String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const lines = [
    headers.map(escapeCsvField).join(','),
    ...exportRows.map(row => headers.map(h => escapeCsvField(row[h])).join(',')),
  ];

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
